import type { OperationalWorkflowTemplateConfig } from '@/config/industry-solutions/types';
import { authService } from '@/lib/authService';
import { createLogger } from '@/lib/debugLogger';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';

const logger = createLogger('fetchWorkflowTemplate');

// Per-template offline cache in the app-shell domain — written on every
// successful live fetch so the template keeps working with no network.
const WORKFLOW_TEMPLATE_CACHE_PREFIX = 'smarttask.workflowTemplate.';

function getClientId(): number | null {
  const user = authService.getUser();
  const direct = Number(user?.client_id);
  if (Number.isFinite(direct) && direct > 0) return direct;
  if (typeof window !== 'undefined') {
    const stored = Number(window.localStorage.getItem('current_client_id'));
    if (Number.isFinite(stored) && stored > 0) return stored;
  }
  return null;
}

function getUserId(): number | null {
  const user = authService.getUser();
  const id = Number(user?.id);
  return Number.isFinite(id) && id > 0 ? id : null;
}

async function cacheWorkflowTemplate(templateKey: string, template: OperationalWorkflowTemplateConfig): Promise<void> {
  const clientId = getClientId();
  if (!clientId) return;
  try {
    const { default: appShellStore } = await import('@/lib/offline/appShellStore');
    await appShellStore.upsertJson(clientId, `${WORKFLOW_TEMPLATE_CACHE_PREFIX}${templateKey}`, template);
  } catch {
    // Best-effort cache — never block the live fetch path
  }
}

async function getCachedWorkflowTemplate(templateKey: string): Promise<OperationalWorkflowTemplateConfig | null> {
  const clientId = getClientId();
  const userId = getUserId();
  if (!clientId) return null;

  // 1. Per-template cache — freshest copy from a previous direct fetch
  try {
    const { default: appShellStore } = await import('@/lib/offline/appShellStore');
    const cached = await appShellStore.getJsonUnifiedOnly<OperationalWorkflowTemplateConfig>(
      clientId,
      `${WORKFLOW_TEMPLATE_CACHE_PREFIX}${templateKey}`,
    );
    if (cached) return cached;
  } catch {
    // fall through to the config bundle
  }

  // 2. The smart-task-config bundle cached at shell startup — covers
  //    workflows the user never opened online.
  if (userId) {
    try {
      const { getSmartTaskRawConfigBundleSnapshot } = await import('@/lib/operations/smartTaskOfflineCache');
      const bundle = await getSmartTaskRawConfigBundleSnapshot(clientId, userId);
      const templates = (bundle?.rawConfigBundle as { workflow_templates?: Record<string, unknown>[] } | undefined)?.workflow_templates;
      const entry = Array.isArray(templates)
        ? templates.find(t => t?.template_key === templateKey)
        : undefined;
      if (entry) {
        return entry as unknown as OperationalWorkflowTemplateConfig;
      }
    } catch {
      // Offline cache unavailable
    }
  }

  return null;
}

export async function fetchWorkflowTemplate(
  templateKey: string
): Promise<OperationalWorkflowTemplateConfig | null> {
  try {
    const response = await fetch(`/api/v1/config/workflows/${encodeURIComponent(templateKey)}`);
    if (response.ok) {
      const template = (await response.json()) as OperationalWorkflowTemplateConfig;
      void cacheWorkflowTemplate(templateKey, template);
      return template;
    }
    logger.error(`[fetchWorkflowTemplate] Failed to fetch "${templateKey}": ${response.status}`);
  } catch (error) {
    if (!isSilentFetchError(error)) {
      logger.error(`[fetchWorkflowTemplate] Error fetching "${templateKey}":`, error);
    }
  }

  // Offline / fetch-failed fallback — serve the last known template
  const cached = await getCachedWorkflowTemplate(templateKey);
  if (cached) {
    logger.debug(`[fetchWorkflowTemplate] Serving "${templateKey}" from offline cache`);
  }
  return cached;
}
